/**
 * Chat text: finding links in it, addressing people, and noticing your own name.
 *
 * Everything here is pure - it returns descriptions of what to build, never
 * markup. Message text comes from other people, so it is only ever put on screen
 * as `textContent` or as an element built from these results; nothing in the app
 * assembles HTML from it.
 */

/**
 * A run of non-space characters that starts a URL. `www.` is included because
 * people write addresses that way, and a topic that reads "www.example.com"
 * should still be clickable.
 */
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;

/**
 * This app's own attachment paths, which messages carry instead of full
 * addresses so that history survives the site moving to another domain.
 *
 * One path segment of safe characters: no slashes, so there is nothing to climb
 * with, and the renderer checks the extension before previewing anything.
 */
const UPLOAD_PATH_PATTERN = /\/uploads\/[A-Za-z0-9][A-Za-z0-9._-]*/g;

/** Punctuation that ends a sentence rather than a URL. */
const SENTENCE_TAIL = '.,;:!?\'"”’«»';
/** Closing brackets, which may equally belong to the URL - see trimTail(). */
const CLOSERS = { ')': '(', ']': '[', '}': '{' };

const countOf = (text, character) => text.split(character).length - 1;

/**
 * Drops trailing characters that punctuate the sentence rather than form part of
 * the address: "see https://example.com/a." or "(https://example.com/b)".
 *
 * A closing bracket is kept when the URL opened one itself, so links like
 * https://en.wikipedia.org/wiki/Cat_(disambiguation) survive intact.
 */
function trimTail(match) {
  let end = match.length;
  while (end > 0) {
    const character = match[end - 1];
    const opener = CLOSERS[character];
    if (opener) {
      const slice = match.slice(0, end);
      if (countOf(slice, character) <= countOf(slice, opener)) break;
    } else if (!SENTENCE_TAIL.includes(character)) {
      break;
    }
    end -= 1;
  }
  return match.slice(0, end);
}

/**
 * The address to navigate to for a piece of matched text, or null when it is not
 * something worth linking.
 *
 * Only http and https are ever returned. The pattern above cannot match a
 * "javascript:" or "data:" URL, and this is the second gate that makes sure of
 * it: whatever arrives, an href is only produced for the two schemes a chat link
 * should ever use.
 */
export function linkHref(raw) {
  const candidate = /^www\./i.test(raw) ? `https://${raw}` : raw;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

/**
 * Splits text into the runs it should be rendered as: `{ text }` for plain
 * stretches and `{ text, href }` for links. The `text` of a link is what the
 * author typed, so nothing about the displayed address is rewritten.
 *
 * Text with no links comes back as a single plain run, so callers can always
 * treat the result the same way.
 */
export function splitLinks(text) {
  const value = String(text ?? '');
  const found = [];

  for (const match of value.matchAll(URL_PATTERN)) {
    const raw = trimTail(match[0]);
    const href = raw ? linkHref(raw) : null;
    if (href) found.push({ at: match.index, text: raw, href });
  }
  // Attachment paths are same-origin by construction, so they are linked as
  // written rather than run through linkHref, which needs a scheme.
  for (const match of value.matchAll(UPLOAD_PATH_PATTERN)) {
    const raw = trimTail(match[0]);
    if (raw) found.push({ at: match.index, text: raw, href: raw });
  }
  found.sort((a, b) => a.at - b.at);

  const runs = [];
  let at = 0;
  for (const link of found) {
    if (link.at < at) continue; // already inside a link that was matched first
    if (link.at > at) runs.push({ text: value.slice(at, link.at) });
    runs.push({ text: link.text, href: link.href });
    at = link.at + link.text.length;
  }
  if (at < value.length) runs.push({ text: value.slice(at) });
  return runs.length > 0 ? runs : [{ text: value }];
}

/** True when a piece of text contains at least one link. */
export function hasLink(text) {
  return splitLinks(text).some((run) => run.href);
}

const isWordCharacter = (character) => character !== '' && /[\p{L}\p{N}_]/u.test(character);

/**
 * True when `body` names `nickname`.
 *
 * Case-insensitive, and only on whole words: "valo" is a mention, "valokuva" is
 * not. The boundary test uses Unicode letter and number properties rather than
 * \b, so that a Finnish name ending in ä is not treated as ending at the ä.
 */
export function mentionsNickname(body, nickname) {
  const haystack = String(body ?? '').toLowerCase();
  const needle = String(nickname ?? '')
    .trim()
    .toLowerCase();
  if (!haystack || !needle) return false;

  for (let from = 0; from <= haystack.length; ) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) return false;
    const before = at === 0 ? '' : haystack[at - 1];
    const after = haystack[at + needle.length] ?? '';
    if (!isWordCharacter(before) && !isWordCharacter(after)) return true;
    from = at + 1;
  }
  return false;
}

/**
 * Works out the text and caret position after dropping `nickname` into a message
 * being composed, at the caret.
 *
 * Opening an empty message addresses the person - "valo: " - which is how people
 * write to someone in a chat room. Anywhere else the name is inserted on its own,
 * with spaces added only where they are missing, so repeated clicks build a list
 * rather than a run of double spaces. A selection is replaced.
 */
export function insertMention({ value = '', selectionStart = null, selectionEnd = null, nickname }) {
  const text = String(value ?? '');
  const name = String(nickname ?? '').trim();
  const start = Number.isInteger(selectionStart) ? selectionStart : text.length;
  const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
  if (!name) return { value: text, cursor: end };

  const before = text.slice(0, start);
  const after = text.slice(end);
  const opensMessage = before.trim() === '';

  const lead = opensMessage || before === '' || /\s$/.test(before) ? '' : ' ';
  const tail = /^\s/.test(after) ? '' : ' ';
  const insertion = `${lead}${name}${opensMessage ? ':' : ''}${tail}`;

  return { value: `${before}${insertion}${after}`, cursor: before.length + insertion.length };
}

/**
 * Works out the text and caret position after dropping a shortcode into a
 * message being composed, at the caret.
 *
 * Spacing follows the same rule as insertMention: a space is added only where
 * one is missing, so picking three emotes in a row gives `:a: :b: :c:` rather
 * than a run of double spaces. Unlike a mention it never punctuates - an emote
 * opening a message is not addressing anyone.
 */
export function insertEmote({ value = '', selectionStart = null, selectionEnd = null, code }) {
  const text = String(value ?? '');
  const name = String(code ?? '').trim();
  const start = Number.isInteger(selectionStart) ? selectionStart : text.length;
  const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
  if (!name) return { value: text, cursor: end };

  const before = text.slice(0, start);
  const after = text.slice(end);
  const lead = before === '' || /\s$/.test(before) ? '' : ' ';
  const tail = /^\s/.test(after) ? '' : ' ';
  const insertion = `${lead}:${name}:${tail}`;

  return { value: `${before}${insertion}${after}`, cursor: before.length + insertion.length };
}

export default { splitLinks, hasLink, linkHref, mentionsNickname, insertMention, insertEmote };
